import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';

import { AdspertPlanType, InvoiceCurrency } from '@app/core/models/customer.models';
import { formatCurrency } from '@app/core/services/metrics-calculations.service';
import { microsToNumber } from '@app/core/utilities/number.utilities';
import { TranslateService } from '@ngx-translate/core';

const plans = [AdspertPlanType.Essential, AdspertPlanType.Scale, AdspertPlanType.Pro];

const allPlans = {
  [AdspertPlanType.Essential]: true,
  [AdspertPlanType.Scale]: true,
  [AdspertPlanType.Pro]: true,
};

const nonEssential = {
  [AdspertPlanType.Essential]: false,
  [AdspertPlanType.Scale]: true,
  [AdspertPlanType.Pro]: true,
};

const onlyEssential = {
  [AdspertPlanType.Essential]: true,
  [AdspertPlanType.Scale]: false,
  [AdspertPlanType.Pro]: false,
};

const onlyPro = {
  [AdspertPlanType.Essential]: false,
  [AdspertPlanType.Scale]: false,
  [AdspertPlanType.Pro]: true,
};

@Component({
  standalone: false,
  selector: 'adspt-pricing-feature-comparison',
  templateUrl: 'pricing-feature-comparison.component.html',
  styleUrls: ['pricing-feature-comparison.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PricingFeatureComparisonComponent {
  readonly ts = inject(TranslateService);

  @Input()
  selectedPlan: AdspertPlanType = AdspertPlanType.Essential;

  @Input()
  analyticsPrice?: number;

  @Input()
  currency?: InvoiceCurrency;

  readonly plans: AdspertPlanType[] = plans;

  readonly planFeatures = {
    'platform-campaign-types': [
      { feature: 'amazon', plans: allPlans },
      { feature: 'ebay', plans: allPlans },
      { feature: 'google', plans: nonEssential },
      { feature: 'microsoft', plans: nonEssential },
    ],
    'ai-driven-ads-optimization': [
      { feature: 'campaign-optimization', plans: allPlans },
      { feature: 'automated-bidding', plans: allPlans },
      { feature: 'bid-adjustments', plans: allPlans },
      { feature: 'campaign-adoption', plans: allPlans },
      { feature: 'dual-goal', plans: nonEssential },
      { feature: 'sales-events', plans: nonEssential },
      { feature: 'intraday-bid-scheduling', plans: allPlans },
      { feature: 'set-up-flexibility', plans: allPlans },
      { feature: 'max-bid-limitation', plans: allPlans },
      { feature: 'min-bid-limitation', plans: nonEssential },
      { feature: 'simulate-goal-impact', plans: allPlans },
    ],
    automations: [
      { feature: 'goal-setup-assistance', plans: allPlans },
      { feature: 'budget-suggestions', plans: allPlans },
      { feature: 'budget-adjustments', plans: allPlans },
      { feature: 'keyword-suggestions', plans: allPlans },
      { feature: 'keyword-harvesting', plans: allPlans },
      { feature: 'keyword-refinement', plans: allPlans },
      { feature: 'keyword-automation', plans: nonEssential },
      { feature: 'product-ads-sync', plans: allPlans },
      { feature: 'alerts', plans: allPlans },
    ],
    'analytics-reporting': [
      { feature: 'multi-platform-dashboard', plans: allPlans },
      { feature: 'monitor-performance', plans: allPlans },
      { feature: 'performance-tracking', plans: allPlans },
      { feature: 'hourly-reports', plans: allPlans },
      { feature: 'custom-reporting', plans: nonEssential },
      { feature: 'historical-data', plans: allPlans },
      { feature: 'product-analytics', plans: allPlans },
      { feature: 'changelogs', plans: allPlans },
      {
        feature: 'analytics',
        plans: {
          [AdspertPlanType.Essential]: () =>
            this.ts.instant('settings.customer-subscription.plan-features.analytics-reporting.analytics-price', {
              price: formatCurrency(microsToNumber(this.analyticsPrice), this.currency, false, 0),
            }),
          [AdspertPlanType.Scale]: true,
          [AdspertPlanType.Pro]: true,
        },
      },
      { feature: 'tacos-revenue-breakdown', plans: allPlans },
    ],
    'support-usage': [
      { feature: 'managed-marketplaces', plans: allPlans },
      { feature: 'email-chat-webinar', plans: allPlans },
      { feature: 'dedicated-csm', plans: nonEssential },
      { feature: 'review-email', plans: onlyEssential },
      { feature: 'review-1on1', plans: nonEssential },
      { feature: 'white-glove-onboarding', plans: onlyPro },
      { feature: 'video-calls-1on1', plans: onlyPro },
      { feature: 'business-reviews', plans: onlyPro },
    ],
  };

  readonly planFeatureSegments = Object.keys(this.planFeatures);

  protected readonly AdspertPlanType = AdspertPlanType;

  getFeatureLabel(labels: Record<string, string>, planItem: string | Function): string {
    if (typeof planItem === 'function') {
      return planItem();
    }

    return labels[planItem] || planItem;
  }
}
